import type {
  ComponentPropsWithoutRef,
  Dispatch,
  FC,
  JSX,
  MouseEventHandler,
  ReactNode,
  SetStateAction,
  SVGProps,
} from 'react'
import type { Icon } from '@tabler/icons-react'

import type { AppVersion, IUser, UserRole } from './auth-interfaces'
import type {
  IDiagnosis,
  IEncounter,
  IExamTemplate,
  IHPIData,
  INote,
  INoteType,
  INoteTypeComponent,
  IPatient,
  ITranscriptContent,
  ITranscriptHistory,
  NoteLengthEnum,
} from './data-interfaces'

export type TActivePageValue =
  | 'login'
  | 'signup'
  | 'ehr_login'
  | 'forgot_password'
  | 'set_new_password'
  | 'check_2fa'

export interface IPageForm {
  setActivePage: Dispatch<SetStateAction<TActivePageValue>>
}

export interface IModal {
  active: boolean
  body: ReactNode
}

export interface IModalStore extends IModal {
  handleSetModal: (values: IModal) => void
  closeModal: () => void
}

export interface ITimer {
  idleTimestamp: number | null
}

export interface ITimerStore extends ITimer {
  touchIdleTimestamp: () => void
  resetTimers: () => void
}

export interface IExamTemplatesState {
  template: IExamTemplate | null
  templateType: IExamTemplate['type']
  showForm: boolean
  templates: IExamTemplate[] | null
}

export interface IExamTemplatesStore extends IExamTemplatesState {
  setTemplate: (template: IExamTemplatesState['template']) => void
  setTemplateType: (currentType: IExamTemplatesState['templateType']) => void
  setShowForm: (currentType: IExamTemplatesState['showForm']) => void
  setTemplates: (templates: IExamTemplatesState['templates']) => void
  reset: () => void
}

export interface INoteStudioState {
  currentType: INoteType | null
  currentComponent: INoteTypeComponent | null
  currentMode: `new-${'type' | 'component'}` | null
  noteTuneCount: number
  enabledSections: Record<number, Record<string, boolean>>
  lastModified: number | null
  savingStatus: SavingStatus | null
  noteTypes: INoteType[] | null
}

export interface INoteStudioStore extends INoteStudioState {
  setCurrentType: (currentType: INoteStudioState['currentType'], resetMode?: boolean) => void
  setCurrentComponent: (
    currentComponent: INoteStudioState['currentComponent'],
    resetMode?: boolean,
  ) => void
  setCurrentMode: (currentMode: INoteStudioState['currentMode']) => void
  setNoteTuneCount: (noteTuneCount: INoteStudioState['noteTuneCount']) => void
  setEnabledSections: (noteTypeId: number, sections: Record<string, boolean>) => void
  setLastModified: (timestamp: number) => void
  reset: () => void
  setSavingStatus: (status: SavingStatus | null) => void
  setNoteTypes: (noteTypes: INoteStudioState['noteTypes']) => void
}

export interface ICalendar {
  className?: string
  handleChange: Dispatch<SetStateAction<Date>> | any
  value: Date | string
  size?: string
  disableBorder?: boolean
}

export interface IDatePicker extends Omit<ICalendar, 'size'> {
  error?: string
  readOnly?: boolean
  placeholder?: string
}

export interface IBtnProps extends ComponentPropsWithoutRef<'button'> {
  variant?: 'outline' | 'pill' | 'link'
  leftIcon?: JSX.Element
  rightIcon?: JSX.Element
  loading?: boolean
}

export interface IFloatingButton {
  icon: JSX.Element
  onClick: () => void
}

export type IToast = (
  msg: string,
  type: 'success' | 'error' | 'info' | 'warning' | 'custom' | 'promise',
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right',
  promise?: any,
  duration?: number,
) => void

export interface ILoaderProps extends ComponentPropsWithoutRef<'div'> {
  size?: 'small' | 'medium' | 'large'
}

export interface IProfileSettingsItem {
  id: string
  title: string
  key?: keyof IUser
}

export type ISettingItem =
  | { field_name: string; value: boolean; casting_type: 'boolean' }
  | { field_name: string; value: string; casting_type: 'string' }
  | { field_name: string; value: string[]; casting_type: 'array' }
  | { field_name: string; value: number; casting_type: 'integer' }

export interface ITranscriptRecordItem {
  startTimeFormatted: string
  startTime: number | undefined
  content: string
}

export interface IEditor {
  onFocus?: () => void
  className?: string
  readOnly?: boolean
  value: string
  onChange?: (value: string) => void
  onSave?: (value: string) => void
  showToolbar?: boolean
  theme?: 'snow' | 'bubble'
  placeholder?: string
}

export interface IConfirmModal {
  icon?: JSX.Element
  children: ReactNode
  onResolve?: () => void
  onReject?: () => void
  rejectBtnTitle?: string
  resolveBtnTitle?: string
  unsetSize?: boolean
  unsetHeight?: boolean
  unsetWidth?: boolean
  hideButtons?: boolean
  onCloseBtnClick?: () => void
  highlightResolveBtn?: boolean
  title?: string
}
export interface ICompleteModal {
  onResolve?: () => void
  onReject?: () => void
  rejectBtnTitle?: string
  resolveBtnTitle?: string
  withBtns?: boolean
  children: ReactNode
  onCloseBtnClick?: () => void
  title?: string
}

export interface IRememberModalProps extends IConfirmModal {
  rememberKey?: string
  onCheckboxChange?: (checked: boolean) => void
}

export interface ITextConfirmModalProps extends IConfirmModal {
  confirmText: string
  purposeText: string
  subtitle?: string
}

export interface IAlertModal {
  message: string
  onClose?: () => void
  closeBtnTitle?: string
}

export interface IProgressBar {
  children?: ReactNode
}

export interface IPromptModal {
  text: string
  onResolve: (value: string) => void
  onReject: () => void
}

export interface IEncounterData {
  item: IEncounter
  className?: string
  withBtn?: boolean
  openAlertModal?: (message: string) => void
  isEhrEnabled?: boolean
  onClick?: () => void
  onClickEncounter?: (encounter: IEncounter) => void
}

export interface IPatientEncountersData {
  patient: IPatient
  encounters: IEncounter[]
  className?: string
}

export interface IPatientItemData {
  patient: IPatient
  isEhrEnabled?: boolean
  onClickEdit?: (patient: IPatient) => void
  onClick?: () => void
  viewOnly?: boolean
  height?: number
  selected?: boolean
}

export interface ILink {
  title: string
  href: string
  icon: FC<SVGProps<SVGSVGElement>> | Icon
  activeIcon?: FC<SVGProps<SVGSVGElement>> | Icon
  external?: boolean
  roles: UserRole[]
  app_versions: AppVersion[]
}

interface IReviewData {
  onSave: () => void
  isCompleted: boolean
  rating?: number
  onSubmitRating: (value: number) => Promise<void>
}

export interface ITranscriptReview extends IReviewData {
  transcriptData: ITranscriptContent[] | null
  setTranscriptData: Dispatch<SetStateAction<ITranscriptContent[]>>
  initialTranscriptData: ITranscriptContent[] | null | undefined
  onRegenerateTranscript: () => void
  onReRecordTranscript: () => void
  onRevertData: () => void
  isClosed: boolean
  transcriptStatus: IEncounter['transcript_status']
}

export interface INoteReview {
  encounter: IEncounter
  voiceEditSave: (updatedNote: INote) => void
  noteData: INote | null
  setNoteData: Dispatch<SetStateAction<INote | null>>
  initialNoteData: INote | null | undefined
  onRegenerateNote: (noteTypeId?: string | null, fieldLength?: NoteLengthEnum) => Promise<void>
  onRevertData: (index: number) => void
  onSave: (updatedNoteData: INote) => Promise<boolean>
  setAddFields?: Dispatch<SetStateAction<boolean>>
  noteStatus: IEncounter['note_status']
  patientAssigned: boolean
  userSettings?: ISettingItem[]
  hpiData?: IHPIData[]
  isCompleted: boolean
  rating?: number
  onSubmitRating: (value: number) => Promise<void>
  generationMode: IEncounter['note_generation_mode']
  previousDiagnoses: IDiagnosis[]
  addDuplicatesRef?: React.MutableRefObject<(() => Promise<void>) | null>
  duplicateDiagnoses: boolean
  handleAddDuplicatesClick: () => void
  isHome?: boolean
  noteType?: INoteType
  handleRegenerateModal: () => void
  newNoteTypeIdRef: React.MutableRefObject<string | null>
  refetchEncounter?: () => void
  isRegenerating: React.MutableRefObject<boolean>
}

export interface IReview {
  transcriptData: ITranscriptContent[] | null
  noteData: INote | null
}

export interface INoteFields {
  noteData: INote | null
  setNoteData: Dispatch<SetStateAction<INote | null>>
  onSave: () => void
  setAddFields: Dispatch<SetStateAction<boolean>>
}

export interface IPatientHistoryProps {
  history: ITranscriptHistory | null
}

export interface IHistoryItem {
  id: string
  transcript_id: string
  history_type: string
  name: string
  active: boolean
  created_at: string
  updated_at: string
}

export interface IPatientHistoryItemProps {
  title: string
  items: IHistoryItem[]
}

export interface IContextMenuItem {
  label: string
  action: MouseEventHandler<HTMLButtonElement>
}

export interface IContextMenuProps {
  items: IContextMenuItem[]
  onClickOutside: () => void
}

export interface IRatingSlider {
  min?: number
  max?: number
  rating: number
  onChange: (value: number) => void
}

export interface IRate {
  initialRating?: number
  onSubmit: (value: number) => Promise<void>
  triggerModal: boolean
  onCloseBtnClick: () => void
}

export interface ISelectPatient {
  closeSelectPatientModal: () => void
  onSelectPatient: (patient_id: string) => void
}

export interface ICalendarDay {
  day: string
  month: string
  year: number
  fullDate: Date
}

export interface IEncountersGroupData {
  encounter: IEncounter
  showCheckbox: boolean
  checked: boolean
  onCheck: (value: boolean) => void
  openAlertModal: (message: string) => void
  isEhrEnabled?: boolean
}

// V1 Interfaces

export interface IBtnPropsV1 extends ComponentPropsWithoutRef<'button'> {
  variant?: 'primary' | 'secondary'
  fill?: 'solid' | 'outline'
  size?: 's' | 'm' | 'l'
  leftIcon?: JSX.Element
  rightIcon?: JSX.Element
  className?: string
}

export enum SavingStatus {
  SAVING = 'saving',
  SAVED = 'saved',
  ERROR = 'error',
}

export interface PillInputProps<T>
  extends Omit<ComponentPropsWithoutRef<'input'>, 'onChange' | 'size'> {
  options: T[]
  value: string
  onChange: (value: T) => void
  getOptionLabel: (option: T) => string
  placeholder?: string
  onSearch?: (searchTerm: string) => Promise<void>
  size?: 'extra_small' | 'small'
}

export enum NoteSectionType {
  HPI = 'HPI',
  ROS = 'ROS',
  PE = 'PE',
  AP = 'AP',
  PATIENT_INSTRUCTIONS = 'patient_instructions',
  DISCUSSION_NOTES = 'discussion_notes',
}
